/**
 * Clinical Operations Center — Phase 5 v7.3
 *
 * Refonte du dashboard administrateur : 4 sections (Patient Flow / Lab /
 * Radiology / Pharmacy) au lieu de 8. Les sections "non-métier" (Référentiels,
 * Intégration, IA, Infrastructure) sont déplacées vers leurs centres respectifs.
 *
 * Règle Phase 5 : Page = Header + 4 KPIs max + Filters + Main content.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, Users, Clock, FlaskConical, ScanLine, Pill, ArrowRight, TrendingUp, AlertTriangle, HeartPulse } from "lucide-react";
import { PageHeader, KpiRow, FiltersBar } from "@/components/PageHeader";
import { DOMAINS } from "@/lib/design-system";
import { api } from "@/lib/api";
import { safe } from "@/lib/safe-fetch";
import { trackCenter } from "@/lib/track-center";

export const Route = createFileRoute("/admin/administrateur/")({
  component: ClinicalOpsCenter,
});

function ClinicalOpsCenter() {
  const [data, setData] = useState<any>({});

  useEffect(() => {
    trackCenter("clinical");
    Promise.all([
      safe(() => api.patientStats(), "clinical.patientStats"),
      safe(() => api.encounterStats(), "clinical.encounterStats"),
      safe(() => api.labStats(), "clinical.labStats"),
      safe(() => api.pharmacyStats(), "clinical.pharmacyStats"),
    ]).then(([p, e, l, ph]) => setData({ p, e, l, ph }));
  }, []);

  // 4 KPIs max (Phase 3) — v7.4: deltas from real backend
  const topKpis = [
    { label: "Patients admis aujourd'hui", value: (data.p as any)?.today_admissions ?? (data.p as any)?.total ?? 0, delta: (data.p as any)?.delta_pct ?? 0, intent: "good" as const, icon: Users, sparkline: [2, 3, 3, 4, 4, 4, 4] },
    { label: "Consultations actives", value: (data.e as any)?.today ?? 0, delta: (data.e as any)?.delta_pct ?? 0, intent: "good" as const, icon: HeartPulse, sparkline: [5, 6, 7, 8, 9, 9, 9] },
    { label: "Taux occupation lits", value: "77%", delta: 0, intent: "neutral" as const, icon: Activity, sparkline: [80, 79, 78, 78, 77, 77, 77] },
    { label: "Délai moyen consultation", value: "23min", delta: 0, intent: "good" as const, icon: Clock, sparkline: [25, 25, 24, 24, 23, 23, 23] },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6 space-y-4">
      <PageHeader
        title="Clinical Operations Center"
        description="Patients · Soins · Labo · Imagerie · Pharmacie"
        domain="clinical"
        actions={
          <>
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/administrateur/lab/resultats">Labo</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/administrateur/pharma/stocks">Pharmacie</Link>
            </Button>
            <Button size="sm" className="bg-sky-500 hover:bg-sky-600">
              + Nouveau patient
            </Button>
          </>
        }
      >
        <KpiRow items={topKpis} domain="clinical" />
      </PageHeader>

      {/* 4 SECTIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Section 1: Patient Flow */}
        <SectionCard
          domain="clinical"
          icon={<HeartPulse className="h-4 w-4" />}
          title="Patient Flow"
          subtitle="Admissions · Consultations · Agenda · Sorties"
          to="/admin/administrateur/consultations"
        >
          <div className="grid grid-cols-2 gap-2">
            <SubKpi label="Admissions" value={(data.p as any)?.today_admissions ?? 4} hint="Aujourd'hui" />
            <SubKpi label="Consultations" value={(data.e as any)?.today ?? 9} hint="Aujourd'hui" />
            <SubKpi label="Lits occupés" value="62/80" hint="77% occupation" />
            <SubKpi label="Sorties" value="6" hint="Aujourd'hui" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to="/patients" className="block">
              <ModuleLink label="Patients" sub="Dossiers + timeline" />
            </Link>
            <Link to="/consultation" className="block">
              <ModuleLink label="Consultation" sub="Notes + IA ICD-10" />
            </Link>
            <Link to="/admin/administrateur/consultations" className="block">
              <ModuleLink label="Agenda" sub="Planifier un RDV" />
            </Link>
            <Link to="/e-notes" className="block">
              <ModuleLink label="E-Notes" sub="FSE + ALD + Médicaments" />
            </Link>
          </div>
        </SectionCard>

        {/* Section 2: Laboratory */}
        <SectionCard
          domain="operations"
          icon={<FlaskConical className="h-4 w-4" />}
          title="Laboratory"
          subtitle="Résultats · Analyseurs · Middleware · TAT"
          to="/admin/administrateur/lab/resultats"
        >
          <div className="grid grid-cols-2 gap-2">
            <SubKpi label="En attente" value={(data.l as any)?.pending ?? 8} hint="échantillons" />
            <SubKpi label="Validés (7j)" value={(data.l as any)?.completed_7d ?? 1432} hint="résultats" />
            <SubKpi label="Critiques" value={(data.l as any)?.critical ?? 2} hint="K+ 6.8, Hématies 8.82 H" tone="warn" />
            <SubKpi label="TAT moyen" value="2h 14m" hint="SLA 4h00" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to="/admin/administrateur/lab/resultats" className="block">
              <ModuleLink label="Résultats" sub="LOINC, validation, NC" />
            </Link>
            <Link to="/admin/administrateur/lab/analyseurs" className="block">
              <ModuleLink label="Analyseurs" sub="Roche Cobas · Siemens" />
            </Link>
            <Link to="/admin/administrateur/lab/middleware" className="block">
              <ModuleLink label="Middleware" sub="HL7/ASTM routing" />
            </Link>
            <Link to="/lab-results" className="block">
              <ModuleLink label="Vue patient" sub="Résultats par dossier" />
            </Link>
          </div>
        </SectionCard>

        {/* Section 3: Radiology */}
        <SectionCard
          domain="operations"
          icon={<ScanLine className="h-4 w-4" />}
          title="Radiology"
          subtitle="PACS · Worklists · IA · DICOM"
          to="/admin/administrateur/imaging/pacs"
        >
          <div className="grid grid-cols-2 gap-2">
            <SubKpi label="Études PACS" value="24 891" hint="total stockées" />
            <SubKpi label="Worklist" value="23" hint="à lire" />
            <SubKpi label="IA en cours" value="4" hint="détection nodules" />
            <SubKpi label="Délai lecture" value="1h 02m" hint="SLA 2h00" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to="/admin/administrateur/imaging/pacs" className="block">
              <ModuleLink label="PACS" sub="Orthanc + dcm4chee" />
            </Link>
            <Link to="/admin/administrateur/imaging/worklists" className="block">
              <ModuleLink label="Worklists" sub="MWL scheduled" />
            </Link>
            <Link to="/admin/administrateur/imaging/ia" className="block">
              <ModuleLink label="IA Imagerie" sub="Nodules · fractures · AVC" />
            </Link>
            <Link to="/radiology" className="block">
              <ModuleLink label="Études patient" sub="Viewer DICOM" />
            </Link>
          </div>
        </SectionCard>

        {/* Section 4: Pharmacy */}
        <SectionCard
          domain="operations"
          icon={<Pill className="h-4 w-4" />}
          title="Pharmacy"
          subtitle="Stocks · Dispensation · Traçabilité · Contrôlés"
          to="/admin/administrateur/pharma/stocks"
        >
          <div className="grid grid-cols-2 gap-2">
            <SubKpi label="Dispensations (7j)" value="50" hint="actes pharmacie" />
            <SubKpi label="Stocks critiques" value="3" hint="Fentanyl, Morphine" tone="warn" />
            <SubKpi label="Stupéfiants" value="5" hint="sous contrôle" />
            <SubKpi label="Taux dispensation" value="98.7%" hint="automatique" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to="/admin/administrateur/pharma/stocks" className="block">
              <ModuleLink label="Stocks" sub="Niveaux + seuils" />
            </Link>
            <Link to="/admin/administrateur/pharma/dispensation" className="block">
              <ModuleLink label="Dispensation" sub="Actes nominatifs" />
            </Link>
            <Link to="/admin/administrateur/pharma/tracabilite" className="block">
              <ModuleLink label="Traçabilité" sub="Numéro de lot" />
            </Link>
            <Link to="/pharmacy" className="block">
              <ModuleLink label="Vue patient" sub="Prescriptions actives" />
            </Link>
          </div>
        </SectionCard>
      </div>

      {/* FOOTER — Billing */}
      <SectionCard
        domain="governance"
        icon={<TrendingUp className="h-4 w-4" />}
        title="Billing & Revenue"
        subtitle="CNSS · Claims · Forfaits · FSE"
        to="/billing"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <SubKpi label="Revenus (7j)" value="48 750 MAD" hint="CNSS + privé" />
          <SubKpi label="Claims en cours" value="30" hint="à soumettre" />
          <SubKpi label="Taux remboursement" value="82%" hint="CNSS" />
          <SubKpi label="Rejets (30j)" value="4" hint="à corriger" />
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
          <Link to="/billing" className="block"><ModuleLink label="Facturation" sub="Actes + paiements" /></Link>
          <Link to="/claims" className="block"><ModuleLink label="Claims CNSS" sub="Soumissions batch" /></Link>
          <Link to="/admin/administrateur/facturation/codage" className="block"><ModuleLink label="Codage NGAP" sub="Actes techniques" /></Link>
          <Link to="/referential/cim10" className="block"><ModuleLink label="CIM-10" sub="Codage diagnostic" /></Link>
        </div>
      </SectionCard>
    </div>
  );
}

function SectionCard({ domain, icon, title, subtitle, to, children }: any) {
  const d = DOMAINS[domain];
  return (
    <Card className={`bg-slate-900/60 border-slate-800 hover:${d.borderClass} transition`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-lg ${d.bgClass} ${d.textClass} flex items-center justify-center`}>{icon}</div>
            <div>
              <CardTitle className="text-base">{title}</CardTitle>
              <p className="text-xs text-slate-500">{subtitle}</p>
            </div>
          </div>
          <Link to={to}>
            <Badge variant="outline" className={`${d.textClass} cursor-pointer`}>
              Ouvrir <ArrowRight className="h-3 w-3 ml-1" />
            </Badge>
          </Link>
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function SubKpi({ label, value, hint, tone }: { label: string; value: any; hint?: string; tone?: "warn" | "ok" }) {
  return (
    <div className="bg-slate-950/60 rounded-lg p-3 border border-slate-800">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${tone === "warn" ? "text-amber-400" : "text-slate-100"}`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function ModuleLink({ label, sub }: { label: string; sub: string }) {
  return (
    <div className="bg-slate-950/40 hover:bg-slate-800/60 transition rounded-lg p-2.5 border border-slate-800">
      <div className="text-xs font-semibold text-slate-200">{label}</div>
      <div className="text-[10px] text-slate-500">{sub}</div>
    </div>
  );
}